import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { map, catchError, of } from 'rxjs';
import { APP_CONSTANTS } from '../constants';
import { isTokenExpired } from '../utils/jwt.utils';
import { AppPaths } from '../routes/app-paths';
import { AuthStore } from '@features/auth/auth.store';
import { AuthService } from '@features/auth/services/auth.service';

/**
 * Guard that checks if user is authenticated.
 * If access token is expired, attempts refresh before redirecting to login.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);

  // Force load from storage if store is empty but localStorage has data
  if (!authStore.user() && !authStore.token() && authService.getStoredSession()) {
    authStore.loadFromStorage();
  }

  // Check if user is authenticated using AuthStore
  const isAuthenticated = authStore.isAuthenticated();

  // List of authentication routes that are accessible without token
  const authRoutes = [
    AppPaths.AUTH.LOGIN,
    AppPaths.AUTH.REGISTER,
    AppPaths.AUTH.REGISTER_FORM,
    AppPaths.AUTH.FORGOT_PASSWORD,
    AppPaths.AUTH.RESET_PASSWORD,
    AppPaths.AUTH.VERIFY_OTP,
    AppPaths.AUTH.CREATE_NEW_PASSWORD,
  ];

  const isAuthRoute = authRoutes.some(authRoute =>
    state.url.includes(`/${AppPaths.AUTH.ROOT}/${authRoute}`),
  );

  // Allow access to mobile app redirect page without authentication
  const isMobileRedirectRoute = state.url.includes('/mobile-app-redirect');

  if (!isAuthenticated) {
    const session = authService.getStoredSession();
    const token = session?.accessToken ?? null;

    // Access token expired: try refresh before redirecting
    if (token && isTokenExpired(token)) {
      return authService.getOrRunRefresh().pipe(
        map(tokens => {
          authService.updateTokens(tokens);
          authStore.loadFromStorage();
          return true;
        }),
        catchError(() => {
          localStorage.removeItem(APP_CONSTANTS.STORAGE.USER);
          if (!isAuthRoute && !isMobileRedirectRoute) {
            router.navigate([`/${AppPaths.AUTH.ROOT}/${AppPaths.AUTH.LOGIN}`], {
              queryParams: { returnUrl: state.url },
            });
          }
          return of(false);
        }),
      );
    }

    // Check if token exists but is expired
    if (token && isTokenExpired(token)) {
      localStorage.removeItem(APP_CONSTANTS.STORAGE.USER);
    }

    // Store says unauthenticated while session key still exists:
    // clear stale session before redirect to login.
    if (session && !isAuthRoute && !isMobileRedirectRoute) {
      localStorage.removeItem(APP_CONSTANTS.STORAGE.USER);
    }

    if (!isAuthRoute && !isMobileRedirectRoute) {
      router.navigate([`/${AppPaths.AUTH.ROOT}/${AppPaths.AUTH.LOGIN}`], {
        queryParams: { returnUrl: state.url },
      });
    }
    return isAuthRoute || isMobileRedirectRoute; // Allow access to auth routes and mobile redirect when not authenticated
  }

  return true;
};
